/**
 * Turning a login response into a signed-in session.
 *
 * Both the password form and the Microsoft sign-in landing page end with the
 * same body shape, so the handling lives here rather than in either page. The
 * password page used to close over its own `email` state; that is why the email
 * is read from the response body instead, the SSO page never saw a form and
 * has no email of its own to close over.
 */

/**
 * Store the session and hand it to UserContext.
 *
 * @param {object} data   The login response body (/api/login or /api/sso/session).
 * @param {function} login  UserContext's login().
 * @returns {string} The path to navigate to next.
 */
export const completeLogin = async (data, login) => {
  localStorage.setItem("access_token", data.access_token);

  // ActivePieces may legitimately be absent, it is never allowed to block a
  // sign-in, so clear any stale token rather than leaving the previous one in
  // place for the next account that signs in on this browser.
  if (data.ap_token) {
    localStorage.setItem("ap_token", data.ap_token);
    localStorage.setItem("projectId", data.ap_project_id);
  } else {
    localStorage.removeItem("ap_token");
    localStorage.removeItem("projectId");
  }

  const mustChangePassword = data.mustChangePassword === true;

  await login(data.userId, data.email ?? null, data.isPlatformAdmin ?? data.isAdmin, {
    isPlatformAdmin: data.isPlatformAdmin ?? data.isAdmin,
    organizationRole: data.organizationRole ?? null,
    organizationId: data.organizationId ?? null,
    mustChangePassword,
  });

  // Accounts still on the emailed temporary password have every other route
  // closed to them server-side; send them somewhere that works. An SSO user
  // never has one, so this only ever fires on the password path.
  return mustChangePassword ? "/change-password" : "/dashboard";
};

export default completeLogin;
